import type { SdkPayments } from '@yuno-payments/sdk-web-types'

// Origins the Web SDK talks to on the checkout path, in production spelling
// (the package maps env 'prod' to an empty environment suffix):
//   sdk-web.y.uno — main.js plus every lazy chunk the SDK pulls after boot
//                   (seamless-checkout, apple-pay-controller, …). Fetched by a
//                   plain <script>, so no crossorigin: a preconnect carrying one
//                   warms the CORS pool the script will not use.
//   api.y.uno     — the REST calls behind Yuno.initialize (initConfig) and
//                   mountSeamlessCheckout (get_settings). Reached by fetch under
//                   CORS, so the preconnect must be anonymous or the browser
//                   opens a second connection anyway and the hint buys nothing.
// The card-form and 3DS micro-apps sit on further hosts (see Yuno's white-label
// proxy doc, which lists sdk-web-card.* and sdk-3ds.*), but their production
// hostnames are not confirmed anywhere we can check from here — a wrong host
// spends a connection slot for nothing, so they are left out rather than
// guessed. Confirm them from a checkout waterfall before adding.
export const YUNO_ORIGINS: { href: string; anonymous: boolean }[] = [
  { href: 'https://sdk-web.y.uno', anonymous: false },
  { href: 'https://api.y.uno', anonymous: true },
]

let yunoSdkPromise: Promise<SdkPayments> | null = null

// Yuno distributes its web SDK by CDN only, so the npm package is a thin loader:
// `loadScript()` injects the official <script> and resolves `window.SdkPayments`
// once it has executed. One call therefore downloads, runs and resolves the SDK
// — there is no second bundle to warm separately the way Primer's shim has.
//
// A rejected load is never cached: the singleton resets so the next call (the
// integration's online retry, the checkout mount, a reinit) re-attempts. This
// was already the right behaviour when the mount was the only caller, but the
// pre-warm makes it load-bearing — it moves the first load to the email step,
// where a visitor who is briefly offline is a real scenario, and a cached
// rejection would leave them with a permanently dead checkout that nothing
// short of a full page reload could recover.
export const loadYunoSdk = (): Promise<SdkPayments> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Yuno SDK requires a browser environment'))
  }
  if (!yunoSdkPromise) {
    yunoSdkPromise = import('@yuno-payments/sdk-web')
      .then((m) => m.loadScript())
      .catch((err) => {
        yunoSdkPromise = null
        throw err
      })
  }
  return yunoSdkPromise
}
